import { useCommandHandlers } from '../../commands/CommandHandlers.jsx'
import { SharedAnchoredVwapContext } from './SharedAnchoredVwap.jsx'
import { createPortal } from 'react-dom'
import { AlarmClock, BellPlus, ChevronDown, LineChart, Trash2, X } from 'lucide-react'
import AnchoredVwapSettings from './AnchoredVwapSettings.jsx'
import { AvwapLabelAlarmRenderer, avwapAlarmLabelTitle } from '../../lib/chart/AvwapLabelAlarmRenderer.js'
import { AnchoredVwapSelectionPrimitive } from '../../lib/chart/AnchoredVwapSelectionPrimitive.js'
import { applyOpacityToColor } from '../../lib/chart/computeIndicator.js'
import { PILL_CLASS } from './chartTokens.js'
import './AnchoredVwapLayer.css'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { retainedVwapAppearance } from '../../lib/chart/retainedVwapAppearance.js'
import { createVwapAppearanceAutosave } from '../../lib/chart/vwapAppearanceAutosave.js'
import { customVwapColor, newCustomVwapAppearance, CUSTOM_VWAP_NEUTRAL } from '../../lib/chart/customVwapStyle.js'
import { vwapRequest } from '../../lib/vwapClient.js'
import { applyRetainedSeries, candleAnchorEpoch, hitTestRetainedVwap, projectRetainedVwap } from '../../lib/chart/retainedVwapPresentation.js'

const DEFAULT_COLOR = CUSTOM_VWAP_NEUTRAL
const labels = { ready: 'Live', preparing: 'Preparing history', recovering: 'Recovering · alerts paused', stale: 'Stale · alerts paused', closed: 'Session closed · alerts paused', unavailable: 'Unavailable · alerts paused' }
const button = 'rounded border border-border px-2 py-1 text-xs text-ink hover:bg-canvas2 disabled:opacity-40 disabled:cursor-not-allowed'
const dateLabel = (time, timezone) => new Date(time * 1000).toLocaleString('en-US', { timeZone: timezone, month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })

export default function AnchoredVwapLayer({ sharedAppearances, onSharedAppearanceChange, currentPrice, labelsVisible = true, alertIconPosition = 'label', chart, container, candlesRef, timeframe, timezone, symbol, pricePaneHeight, toolbarTarget = null, supported = true, request = vwapRequest, appearanceStore = retainedVwapAppearance }) {
  const shared = useContext(SharedAnchoredVwapContext)
  const sharedEnabled = Boolean(shared)
  const labelAlarms = useRef(null)
  useEffect(() => {
    if (!chart || !container) return
    const renderer = new AvwapLabelAlarmRenderer(container)
    labelAlarms.current = renderer
    return () => { renderer.destroy(); labelAlarms.current = null }
  }, [chart, container])
  const [snapshots, setSnapshots] = useState([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [menu, setMenu] = useState(null)
  const [expanded, setExpanded] = useState(false)
  useCommandHandlers(toolbarTarget ? { 'position.chart.vwaps.open': () => { setSelected(''); setExpanded(true) } } : {})
  useEffect(() => { if (!toolbarTarget) setExpanded(false) }, [toolbarTarget])
  const [selected, setSelected] = useState('')
  const [picked, setPicked] = useState('')
  const [settingsTab, setSettingsTab] = useState('style')
  const [direction, setDirection] = useState('crossing')
  const [alertSettings, setAlertSettings] = useState({trigger:'once',expiresAt:null,message:''})
  const [localAppearances, setAppearances] = useState({})
  const appearances = sharedAppearances ?? localAppearances
  // A supplied null means the workspace price is unavailable; do not silently
  // substitute a historical close. Standalone charts may use their latest bar.
  const latestPrice = currentPrice === undefined ? candlesRef.current.at(-1)?.close : currentPrice
  const [appearanceMessages, setAppearanceMessages] = useState({})
  const appearancesRef = useRef(appearances)
  appearancesRef.current = appearances
  const editedAppearances = useRef(new Set())
  const loadedAppearances = useRef(new Set())
  const anchorIds = snapshots.map(s => s.id).join(',')
  useEffect(() => {
    if (sharedAppearances !== undefined) return
    let active = true
    for (const id of anchorIds.split(',').filter(Boolean)) {
      if (loadedAppearances.current.has(id)) continue
      appearanceStore.load(id).then(value => {
        if (!active) return
        loadedAppearances.current.add(id)
        if (!editedAppearances.current.has(id)) setAppearances(old => ({ ...old, [id]: value }))
      }).catch(() => {})
    }
    return () => { active = false }
  }, [anchorIds, appearanceStore, sharedAppearances])
  const [localHidden, setHidden] = useState(new Set())
  const hidden = useMemo(() => sharedAppearances === undefined ? localHidden : new Set(Object.entries(sharedAppearances).filter(([, value]) => value.visible === false).map(([id]) => id)), [sharedAppearances, localHidden])
  const series = useRef(new Map())
  const currentSymbol = useRef(symbol)
  const refreshRef = useRef(null)
  const mutation = useRef(0)
  const mounted = useRef(false)
  const autosave = useMemo(() => createVwapAppearanceAutosave(
    (id, value) => appearanceStore.save(id, value),
    (id, message) => { if (mounted.current) setAppearanceMessages(old => ({ ...old, [id]: message })) },
  ), [appearanceStore])
  useEffect(() => () => { void autosave.flushAll() }, [autosave])
  useEffect(() => {
    if (expanded && selected) return () => { void autosave.flush(selected) }
  }, [expanded, selected, autosave])
  const toolbarButton = useRef(null)
  const panel = useRef(null)
  const [panelPosition, setPanelPosition] = useState({ left: 8, top: 8 })
  useEffect(() => {
    if (!expanded) return
    const place = () => {
      if (selected) {
        setPanelPosition({ left: Math.max(8, (window.innerWidth - 272) / 2), top: Math.max(8, (window.innerHeight - 340) / 2) }); return
      }
      const rect = toolbarButton.current?.getBoundingClientRect()
      if (!rect) return
      setPanelPosition({ left: Math.max(8, Math.min(rect.right - 288, window.innerWidth - 296)), top: Math.max(8, Math.min(rect.bottom + 4, window.innerHeight - 336)) })
    }
    const dismiss = e => {
      if (e.type === 'keydown') { if (e.key === 'Escape') setExpanded(false); return }
      if (e.target.closest?.('[data-color-opacity-popover="true"]')) return
      if (!panel.current?.contains(e.target) && !toolbarButton.current?.contains(e.target) && !e.target.closest?.('[data-vwap-menu]')) setExpanded(false)
    }
    place()
    document.addEventListener('pointerdown', dismiss); document.addEventListener('keydown', dismiss)
    window.addEventListener('resize', place); window.addEventListener('scroll', place, true)
    return () => {
      document.removeEventListener('pointerdown', dismiss); document.removeEventListener('keydown', dismiss)
      window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true)
    }
  }, [expanded, toolbarTarget, selected])
  currentSymbol.current = symbol

  const refresh = useCallback(async signal => {
    const generation = mutation.current
    try {
      const list = await request({ signal })
      const matching = list.anchors.filter(a => a.symbol === symbol)
      const next = []
      // Bound each refresh and avoid a simultaneous burst of snapshot reads.
      for (const a of matching) next.push(await request({ id: a.id, signal }))
      if (signal.aborted || currentSymbol.current !== symbol || generation !== mutation.current) return
      setSnapshots(next); setError('')
    } catch (e) {
      if (!signal.aborted && currentSymbol.current === symbol && generation === mutation.current) setError(e.message)
    }
  }, [request, symbol])

  useEffect(() => {
    if (sharedEnabled) {
      mounted.current = true
      setSnapshots([]); setMenu(null); setSelected(''); setError('')
      return () => { mounted.current = false; refreshRef.current = null }
    }
    let active = true, timer, controller
    mounted.current = true
    setSnapshots([]); setMenu(null); setSelected(''); setError('')
    const poll = async () => {
      clearTimeout(timer); controller?.abort(); controller = new AbortController()
      const current = controller, signal = current.signal
      let timedOut = false
      const deadline = setTimeout(() => {
        if (active && !signal.aborted) { timedOut = true; current.abort(); setError('VWAP connection timed out') }
      }, 10000)
      if (supported) await refresh(signal)
      clearTimeout(deadline)
      if (active && current === controller && (!signal.aborted || timedOut)) timer = setTimeout(poll, 1000)
    }
    refreshRef.current = poll
    const resume = () => { if (!document.hidden) poll() }
    poll()
    window.addEventListener('focus', resume); window.addEventListener('pageshow', resume); window.addEventListener('online', resume)
    document.addEventListener('visibilitychange', resume)
    return () => {
      active = false; mounted.current = false; clearTimeout(timer); controller?.abort(); refreshRef.current = null
      window.removeEventListener('focus', resume); window.removeEventListener('pageshow', resume); window.removeEventListener('online', resume)
      document.removeEventListener('visibilitychange', resume)
    }
  }, [refresh, supported, sharedEnabled])

  useEffect(() => {
    if (!shared) return
    setSnapshots(supported ? shared.snapshots : [])
    setError(shared.error)
    refreshRef.current = shared.refresh
  }, [shared, supported])

  useEffect(() => {
    series.current = new Map()
    return () => {
      for (const entry of series.current.values()) { try { entry.series.detachPrimitive?.(entry.marker); chart?.removeSeries(entry.series) } catch {} }
      series.current.clear()
    }
  }, [chart])

  useEffect(() => {
    if (!chart) return
    try {
      const range = chart.timeScale().getVisibleLogicalRange()
      const ids = new Set(snapshots.map(s => s.id))
      for (const [id, entry] of series.current) if (!ids.has(id)) { entry.series.detachPrimitive?.(entry.marker); chart.removeSeries(entry.series); series.current.delete(id) }
      for (const [i, snapshot] of snapshots.entries()) {
        let entry = series.current.get(snapshot.id)
        if (!entry) {
          entry = { series: chart.addLineSeries({ color: DEFAULT_COLOR, lineWidth: 2, crosshairMarkerVisible: false, priceLineVisible: false, lastValueVisible: true, title: `AVWAP ${i + 1}`, autoscaleInfoProvider: () => null }, 0), points: [] }
          entry.marker = new AnchoredVwapSelectionPrimitive()
          entry.series.attachPrimitive?.(entry.marker)
          series.current.set(snapshot.id, entry)
        }
        const points = projectRetainedVwap(snapshot, candlesRef.current, timeframe, timezone)
        applyRetainedSeries(entry.series, entry.points, points); entry.points = points
        const anchorPoint = snapshot.points?.[0]
        const projectedAnchor = anchorPoint && projectRetainedVwap({ ...snapshot, tailTime: null, points: [anchorPoint] }, candlesRef.current, timeframe, timezone)[0]
        const point = projectedAnchor && points.find(p => p.time === projectedAnchor.time)
        entry.anchorPoint = point
      }
      const after = chart.timeScale().getVisibleLogicalRange()
      if (range && after && (range.from !== after.from || range.to !== after.to)) chart.timeScale().setVisibleLogicalRange(range)
    } catch { setError('VWAP line could not be rendered') }
  }, [chart, snapshots, timeframe, timezone, candlesRef])

  useEffect(() => {
    try {
      const labelEntries = new Map()
      labelAlarms.current?.setEntries(labelEntries)
      for (const [i, snapshot] of snapshots.entries()) {
        const entry = series.current.get(snapshot.id)
        if (!entry) continue
        const appearance = appearances[snapshot.id] || {}
        const color = applyOpacityToColor(customVwapColor(appearance, latestPrice), appearance.opacity ?? 100)
        entry.hidden = hidden.has(snapshot.id)
        const armed = snapshot.armed && !snapshot.alertExpired
        const showLabel = labelsVisible && appearance.priceAxisVisible !== false
        const onLabel = alertIconPosition === 'label' && showLabel
        const name = appearance.name || `AVWAP ${i + 1}`
        const title = armed && onLabel ? avwapAlarmLabelTitle(name, i) : name
        if (armed && onLabel && !entry.hidden) labelEntries.set(title, color)
        entry.marker.setAlert(armed && !onLabel && !entry.hidden ? {
          point: entry.points.at(-1),
          color,
        } : null)
        entry.series.applyOptions({ color, title: showLabel ? title : '', lastValueVisible: showLabel, lineWidth: appearance.lineWidth || 2, visible: !entry.hidden, lineStyle: appearance.lineStyle || 0 })
      }
    } catch { setError('VWAP appearance could not be rendered') }
  }, [chart, snapshots, appearances, hidden, error, timeframe, timezone, candlesRef, alertIconPosition, labelsVisible, container, latestPrice])

  useEffect(() => {
    for (const [id, entry] of series.current) {
      const anchorVisible = entry.anchorPoint && candlesRef.current.some(c => c.time === entry.anchorPoint.time)
      entry.marker.setPoint(picked === id && !hidden.has(id) && anchorVisible ? entry.anchorPoint : null)
    }
  }, [chart, snapshots, timeframe, timezone, candlesRef, picked, hidden])

  useEffect(() => {
    if (!container || !chart) return
    const open = event => {
      const rect = container.getBoundingClientRect(), x = event.clientX - rect.left, y = event.clientY - rect.top
      if (x < 0 || x > chart.timeScale().width() || y < 0 || y >= pricePaneHeight) return
      const hit = hitTestRetainedVwap(series.current, chart.timeScale(), x, y)
      const position = { x: Math.max(8, Math.min(event.clientX, window.innerWidth - 256)), y: Math.max(8, Math.min(event.clientY, window.innerHeight - 180)) }
      if (hit) {
        event.preventDefault(); event.stopPropagation()
        setMenu({ id: hit, ...position }); return
      }
      const time = chart.timeScale().coordinateToTime(x)
      const candle = candlesRef.current.find(b => b.time === time)
      const anchor = candleAnchorEpoch(candle, timeframe, timezone)
      if (!anchor || !supported) return
      event.preventDefault(); event.stopPropagation()
      setMenu({ anchor, timeframe, appearance: newCustomVwapAppearance(candle), lifetime: ['daily','weekly','monthly'].includes(timeframe) ? 'open' : '1D', ...position })
    }
    container.addEventListener('contextmenu', open)
    return () => container.removeEventListener('contextmenu', open)
  }, [chart, container, candlesRef, timeframe, timezone, pricePaneHeight, supported])

  useEffect(() => {
    if (!container || !chart) return
    const edit = event => {
      const rect = container.getBoundingClientRect(), x = event.clientX - rect.left, y = event.clientY - rect.top
      if (x < 0 || x > chart.timeScale().width() || y < 0 || y >= pricePaneHeight) return
      const id = hitTestRetainedVwap(series.current, chart.timeScale(), x, y)
      if (!id) return
      event.preventDefault(); event.stopImmediatePropagation(); setPicked(id); openSettings(id, 'style')
    }
    container.addEventListener('dblclick', edit, true)
    return () => container.removeEventListener('dblclick', edit, true)
  }, [chart, container, pricePaneHeight, snapshots])

  useEffect(() => {
    if (!container || !chart) return
    const click = event => {
      if (event.shiftKey) return
      const rect = container.getBoundingClientRect(), x = event.clientX - rect.left, y = event.clientY - rect.top
      setPicked(y >= 0 && y < pricePaneHeight && x >= 0 && x < chart.timeScale().width() ? hitTestRetainedVwap(series.current, chart.timeScale(), x, y) || '' : '')
    }
    const clear = event => {
      if (event.type === 'keydown') { if (event.key === 'Escape') setPicked(''); return }
      if (container.contains(event.target) || panel.current?.contains(event.target) || toolbarButton.current?.contains(event.target) || event.target.closest?.('[data-vwap-menu], [data-color-opacity-popover="true"]')) return
      setPicked('')
    }
    container.addEventListener('click', click)
    document.addEventListener('pointerdown', clear); document.addEventListener('keydown', clear)
    return () => { container.removeEventListener('click', click); document.removeEventListener('pointerdown', clear); document.removeEventListener('keydown', clear) }
  }, [chart, container, pricePaneHeight])

  useEffect(() => {
    if (!menu) return
    const close = e => { if (e.type === 'keydown' && e.key !== 'Escape') return; if (e.target?.closest?.('[data-vwap-menu]')) return; setMenu(null) }
    document.addEventListener('pointerdown', close); document.addEventListener('keydown', close)
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', close) }
  }, [menu])

  async function mutate(body, initialAppearance) {
    const target = symbol
    setBusy(true); setMenu(null); setError(''); mutation.current++
    shared?.invalidate()
    try {
      const value = await request({ body, ownerId: shared?.ownerId })
      if (!mounted.current || currentSymbol.current !== target) return
      mutation.current++
      if (body.action === 'create') {
        editAppearance(value.id, initialAppearance, { initialize: true })
        setSelected(''); setExpanded(false); setSnapshots(old => [...old.filter(s => s.id !== value.id), value])
      }
      else if (body.action === 'delete') { setSnapshots(old => old.filter(s => s.id !== body.id)); setSelected('') }
      else setSnapshots(old => old.map(s => s.id === value.id ? value : s))
      await refreshRef.current?.()
    } catch (e) { if (mounted.current && currentSymbol.current === target) { setError(e.message); void shared?.refresh() } }
    finally { if (mounted.current && currentSymbol.current === target) setBusy(false) }
  }
  function openSettings(id, tab = 'style') {
    const snapshot = snapshots.find(s => s.id === id)
    setAlertSettings(snapshot?.alertSettings || {trigger:'once',expiresAt:null,message:''});
    setSettingsTab(tab); setSelected(id); setDirection(snapshot?.alert || (snapshot?.alertSettingsVersion === 1 ? 'crossing' : 'crosses_above')); setExpanded(true); setMenu(null)
  }
  function editAppearance(id, patch, options) {
    editedAppearances.current.add(id)
    const value = { ...appearancesRef.current[id], ...patch }
    appearancesRef.current = { ...appearancesRef.current, [id]: value }
    if (onSharedAppearanceChange) { void onSharedAppearanceChange(id, patch, options); return }
    setAppearances(appearancesRef.current)
    autosave.edit(id, value)
  }
  const menuSnapshot = snapshots.find(s => s.id === menu?.id)
  const active = snapshots.find(s => s.id === selected)
  const pendingCount = snapshots.filter(s => !['ready', 'closed'].includes(s.status)).length
  const toggleHidden = id => {
    if (onSharedAppearanceChange) { editAppearance(id, { visible: hidden.has(id) }); return }
    setHidden(old => { const next = new Set(old); next.has(id) ? next.delete(id) : next.add(id); return next })
  }

  return <>
    {menu && createPortal(<div role="menu" aria-label={menu.id ? 'VWAP actions' : 'Candle actions'} data-vwap-menu className="avwap-context-menu" style={{ left: menu.x, top: menu.y }}>
      {menu.id ? <>
        <button autoFocus role="menuitem" className="avwap-context-row" onClick={() => openSettings(menu.id, 'alerts')}><BellPlus size={18} strokeWidth={1.5} aria-hidden />{menuSnapshot?.armed ? 'Manage alert…' : 'Set alert…'}</button>
        <div role="separator" className="avwap-context-separator" />
        <button role="menuitem" disabled={busy} className="avwap-context-row" onClick={() => mutate({ action: 'delete', id: menu.id })}><Trash2 size={18} strokeWidth={1.5} aria-hidden />Remove anchored VWAP</button>
      </> : <>
        <label className="block px-3 py-2 text-xs">Keep anchor for<select aria-label="Anchor expiration" className="ml-2 rounded border border-border bg-canvas px-2 py-1 text-ink" value={menu.lifetime} onChange={e => setMenu(old => ({...old,lifetime:e.target.value}))}>
          <option value="1D">1D</option><option value="2D">2D</option><option value="1W">1W</option><option value="open">Open-ended</option>
        </select></label>
        <button autoFocus role="menuitem" disabled={busy} className="avwap-context-row" onClick={() => mutate({ action: 'create', symbol, anchor: menu.anchor, timeframe: menu.timeframe, lifetime: menu.lifetime }, menu.appearance)}><LineChart size={18} strokeWidth={1.5} aria-hidden />Add anchored VWAP</button>
      </>}
    </div>, document.body)}
    {toolbarTarget && createPortal(<button ref={toolbarButton} data-command-id="position.chart.vwaps.open" className={`${PILL_CLASS.base} ${expanded ? PILL_CLASS.active : PILL_CLASS.inactive} flex items-center gap-1 whitespace-nowrap`} onClick={() => { setSelected(''); setExpanded(v => !v) }} aria-expanded={expanded} aria-haspopup="dialog" aria-label={`Anchored VWAPs${snapshots.length ? ` (${snapshots.length})` : ''}${error ? ' · Unavailable' : ''}`}>
      <LineChart size={14} strokeWidth={1.5} aria-hidden /><span>AVWAPs{snapshots.length ? ` (${snapshots.length})` : ''}</span>{(pendingCount > 0 || error) && <span role="status" aria-label={error ? 'VWAP connection unavailable' : `${pendingCount} VWAPs not live`} title={error || 'Open saved VWAPs for readiness'} className="text-amber-500">•</span>}<ChevronDown size={12} aria-hidden />
    </button>, toolbarTarget)}
    {expanded && createPortal(<div ref={panel} role="dialog" aria-label="Anchored VWAPs" className={`fixed z-50 max-h-[85vh] overflow-auto rounded-md border border-slate-200 bg-white shadow-lg text-slate-900 text-xs dark:border-white/10 dark:bg-[#0e1116] dark:text-white dark:shadow-black/30 ${active ? 'avwap-settings-panel' : 'w-72 p-3'}`} style={panelPosition}>
      {active ? <AnchoredVwapSettings snapshot={active} appearance={appearances[active.id] || {}} defaultName={`AVWAP ${snapshots.indexOf(active) + 1}`} defaultColor={customVwapColor(appearances[active.id], latestPrice)} tab={settingsTab} onTab={setSettingsTab} onChange={patch => editAppearance(active.id, patch)} onRetry={() => autosave.flush(active.id)} onLifetime={lifetime => mutate({action:'lifetime',id:active.id,lifetime})} onRemove={() => mutate({ action: 'delete', id: active.id })} onClose={() => setExpanded(false)} message={appearanceMessages[active.id] || ''} busy={busy} error={error} status={labels[active.status] || active.status} direction={direction} onDirection={setDirection} alertSettings={alertSettings} onAlertSettings={patch => setAlertSettings(old => ({...old,...patch}))} onAlert={condition => mutate({ action: 'alert', id: active.id, condition, ...(active.alertSettingsVersion === 1 && condition ? {settings:alertSettings} : {}) })} visible={!hidden.has(active.id)} onVisible={() => toggleHidden(active.id)} dateLabel={time => dateLabel(time, timezone)} /> : <>
        <div className="mb-3 flex items-center justify-between"><span className="font-medium">Saved VWAPs</span><button aria-label="Close VWAP settings" onClick={() => setExpanded(false)}><X size={16} aria-hidden /></button></div>
        {!supported && <p>Anchored VWAP supports U.S. stocks and ETFs.</p>}
        {error && <p role="alert" className="text-amber-500">{error}</p>}
        {!snapshots.length && <p>No saved anchors for {symbol}.</p>}
        {snapshots.map((s, i) => <div key={s.id} className="mt-2 flex items-center gap-2 border-t border-slate-200 pt-2 dark:border-white/10">
          <button className="min-w-0 flex-1 text-left" onClick={() => openSettings(s.id)}><span style={{ color: customVwapColor(appearances[s.id], latestPrice) }}>{appearances[s.id]?.name || `AVWAP ${i + 1}`}{s.armed && !s.alertExpired && <AlarmClock size={10} className="ml-1 inline opacity-60" aria-label="Alert armed" />}</span>{s.points?.length ? ` · $${s.points.at(-1).value.toFixed(4)}` : ''}<span className="block text-slate-500 dark:text-white/60" title={error || s.reason || ''}>{error ? 'Unavailable · alerts paused' : labels[s.status] || s.status}</span></button>
          <button className={button} aria-label={`${hidden.has(s.id) ? 'Show' : 'Hide'} AVWAP ${i + 1}`} onClick={() => toggleHidden(s.id)}>{hidden.has(s.id) ? 'Show' : 'Hide'}</button>
        </div>)}
      </>}
    </div>, document.body)}
  </>
}
